import filter from 'lodash/filter';
import find from 'lodash/find';
import includes from 'lodash/includes';
import moment from 'moment';
import {getWindow} from 'ssr-window';
import Arg from '@spothero/arg';
import Config from 'config';
import TimeUtils from 'utils/time';

const window = getWindow();

const CheckoutUtils = {
    PAYMENT_REQUEST_TYPES: {
        APPLE_PAY: 'apple_pay',
        GOOGLE_PAY: 'android_pay',
    },

    selectRate({spot, isMonthly, rid}) {
        let selectedRate = isMonthly
            ? spot.monthly_rates[0]
            : spot.hourly_rates[0];

        if (rid) {
            if (isMonthly) {
                selectedRate = filter(spot.monthly_rates, rate => {
                    return rate.rule_id === parseInt(rid, 10);
                });
            } else {
                selectedRate = filter(spot.hourly_rates, rate => {
                    return rate.rule_group_id === parseInt(rid, 10);
                });
            }

            selectedRate = selectedRate.length ? selectedRate[0] : null;
        }

        return selectedRate;
    },

    getMonthlyStart(reservationState, newSelectedRate) {
        const {monthly: isMonthly, starts} = reservationState;
        let newMonthlyStarts = starts;

        // if monthly and start date restrictions present
        // default starts to the 1st of the month (when not already)
        if (
            isMonthly &&
            newSelectedRate &&
            newSelectedRate.start_date_restrictions !== 'none'
        ) {
            const monthlyStartsDay = starts.split('-')[2];
            let updateMonthlyStartDate = false;

            if (newSelectedRate.start_date_restrictions === '1st_only') {
                if (monthlyStartsDay !== '01') {
                    updateMonthlyStartDate = true;
                }
            } else {
                if (monthlyStartsDay !== '01' && monthlyStartsDay !== '15') {
                    updateMonthlyStartDate = true;
                }
            }

            if (updateMonthlyStartDate) {
                const selectedRateStartObj = moment(newSelectedRate.starts);

                if (
                    newSelectedRate.start_date_restrictions === '1st_or_15th' &&
                    selectedRateStartObj.date() > 14
                ) {
                    newMonthlyStarts = selectedRateStartObj
                        .date(15)
                        .format(Config.apiDateTimeFormat);
                } else {
                    newMonthlyStarts = selectedRateStartObj
                        .date(1)
                        .format(Config.apiDateTimeFormat);
                }
            }
        }

        return newMonthlyStarts;
    },

    getTransientReservationInfo({
        reservationStarts,
        reservationEnds,
        rateStarts,
        rateEnds,
        format = 'ddd MMM D [at] h:mma',
    }) {
        const startDateTimeObj = moment(rateStarts);
        const endDateTimeObj = moment(rateEnds);

        return {
            startDateTimeFormatted: startDateTimeObj.format(format),
            endDateTimeFormatted: endDateTimeObj.format(format),
            duration: TimeUtils.prettyDuration(
                endDateTimeObj.diff(startDateTimeObj)
            ),
            timeExtended:
                startDateTimeObj < moment(reservationStarts) ||
                endDateTimeObj > moment(reservationEnds),
        };
    },

    getMonthlyReservationInfo(selectedRate, starts) {
        const dateTimeShortFormat = 'MMM D';
        const startDateTimeObj = moment(starts || selectedRate.starts);
        const {reservation_dates: reservationDates} = selectedRate;
        const invoiceStartDateTimeObj = moment(reservationDates[0].starts);
        const invoiceEndDateTimeObj = moment(reservationDates[0].ends);
        const invoiceNextStartDateTimeObj =
            reservationDates.length > 1
                ? moment(reservationDates[1].starts)
                : null;
        const invoiceNextEndDateTimeObj = invoiceNextStartDateTimeObj
            ? moment(reservationDates[1].ends)
            : null;

        return {
            startDateFormatted: startDateTimeObj.format(dateTimeShortFormat),
            endDateFormatted: startDateTimeObj
                .clone()
                .add(1, 'months')
                .subtract(1, 'days')
                .format(dateTimeShortFormat),
            invoiceStartDate: invoiceStartDateTimeObj.format(
                Config.apiDateTimeFormat
            ),
            invoiceStartDateFormatted: invoiceStartDateTimeObj.format(
                dateTimeShortFormat
            ),
            invoiceEndDateFormatted: invoiceEndDateTimeObj.format(
                dateTimeShortFormat
            ),
            invoiceNextStartDate: invoiceNextStartDateTimeObj
                ? invoiceNextStartDateTimeObj.format(Config.apiDateTimeFormat)
                : null,
            invoiceNextStartDateFormatted: invoiceNextStartDateTimeObj
                ? invoiceNextStartDateTimeObj.format(dateTimeShortFormat)
                : null,
            invoiceNextEndDateFormatted: invoiceNextEndDateTimeObj
                ? invoiceNextEndDateTimeObj.format(dateTimeShortFormat)
                : null,
        };
    },

    buildURLFromParams(url, params) {
        const urlSplit = url.split('/');
        let facilityUrl = url;

        if (includes(urlSplit[0], 'http')) {
            facilityUrl = `/${urlSplit.slice(3).join('/')}`;
        }

        return `${facilityUrl}?${Arg.stringify(params)}`;
    },

    updateRoute({currentUrl, searchParams, adParams}) {
        const cleanedUrl = currentUrl.split(/[?#]/)[0];
        const hash = window.location.hash ? window.location.hash : '';
        const {
            monthly,
            airport,
            starts,
            ends,
            rid,
            rebook_reservation_id: rebookReservationId,
        } = searchParams;
        const urlParams = {
            starts,
            ...adParams,
        };

        if (monthly) {
            urlParams.monthly = monthly;
        } else {
            urlParams.ends = ends;
        }

        if (airport) {
            urlParams.airport = airport;
        }

        if (rid) {
            urlParams.rid = rid;
        }

        if (rebookReservationId) {
            urlParams.rebook_reservation_id = rebookReservationId; // eslint-disable-line camelcase
        }

        return `${cleanedUrl}?${Arg.stringify(urlParams).replace(
            /%3A%3A/g,
            '::'
        )}${hash}`;
    },

    getAmountToCharge({price, discount, userCredit}) {
        let amountToCharge = 0;
        let creditUsed = 0;

        if (price) {
            amountToCharge = discount ? price - discount : price;
        }

        if (userCredit) {
            creditUsed =
                userCredit < amountToCharge ? userCredit : amountToCharge;
            amountToCharge -= creditUsed;
        }

        return {
            amountToCharge: amountToCharge > 0 ? amountToCharge : 0,
            creditUsed,
        };
    },

    getOversizeFeeFromPriceBreakdown(priceBreakdown) {
        // using this in lieu of a default param since it won't catch null
        // priceBreakdown is frequently null
        const workingPriceBreakdown = priceBreakdown || {};

        return find(workingPriceBreakdown.items, {
            type: 'oversize_vehicle_fee_flat',
        });
    },
};

export default CheckoutUtils;
